import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, RefreshCw, Radio, ExternalLink, Search, Play, Pause, Square, CheckCircle2, AlertTriangle, Copy, Youtube } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const input = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function StatusPill({ conn }) {
  let label = "Ready", cls = "bg-gray-100 text-gray-600";
  if (conn.last_error) { label = "Error"; cls = "bg-red-100 text-red-700"; }
  else if (conn.state === "complete") { label = "Complete"; cls = "bg-green-100 text-green-700"; }
  else if (conn.live_tracking_enabled) { label = "Live"; cls = "bg-red-100 text-red-700"; }
  else if (conn.public_enabled) { label = "Public"; cls = "bg-blue-100 text-blue-700"; }
  return <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${cls}`} data-testid={`cl-status-${conn.local_tournament_id}`}>{label}</span>;
}

function ConnectionCard({ conn, usage, limit, onChange }) {
  const ltid = conn.local_tournament_id;
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [feedTitle, setFeedTitle] = useState("");
  const [feedBody, setFeedBody] = useState("");
  const [stream, setStream] = useState(conn.livestream_url || "");

  const call = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const set = (patch, msg) => call(() => api.put(`/admin/challonge/connection/${ltid}`, patch), msg);
  const syncNow = () => call(async () => { const { data } = await api.post(`/admin/challonge/connection/${ltid}/sync`); toast.success(`Synced — ${data.new_results} new result(s)`); });
  const remove = () => { if (window.confirm("Disconnect this tournament? Local live data will be removed.")) call(() => api.delete(`/admin/challonge/connection/${ltid}`), "Disconnected"); };
  const loadLogs = async () => {
    const { data } = await api.get(`/admin/challonge/connection/${ltid}`);
    setLogs(data.logs || []); setShowLog((s) => !s);
  };
  const postFeed = () => { if (!feedTitle.trim()) return toast.error("Title required"); call(async () => { await api.post(`/admin/challonge/connection/${ltid}/feed`, { title: feedTitle, body: feedBody }); setFeedTitle(""); setFeedBody(""); }, "Update posted"); };

  const budgetWarn = usage >= 450 ? "Automatic syncing paused (budget)" : usage >= 400 ? "Slowed to ~5 min (budget)" : null;

  return (
    <div className="rounded-lg border border-[#E5E7EB] p-5 space-y-4" data-testid={`cl-conn-${ltid}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-bold text-lg">{conn.name}</h3>
            <StatusPill conn={conn} />
          </div>
          <p className="text-xs text-[#777]">Local ID: <code>{ltid}</code> · {conn.tournament_type} · Challonge Connected ✓</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={`/tournaments/${ltid}/live`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#1E63C4] font-semibold" data-testid={`cl-open-live-${ltid}`}><ExternalLink className="h-4 w-4" /> Live page</a>
          <a href={conn.challonge_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#777]"><ExternalLink className="h-4 w-4" /> Challonge</a>
        </div>
      </div>

      {conn.last_error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {conn.last_error}</p>}
      {budgetWarn && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">{budgetWarn}</p>}

      <div className="grid sm:grid-cols-2 gap-3">
        <Toggle label="Live Tracking" on={conn.live_tracking_enabled} onClick={() => set({ live_tracking_enabled: !conn.live_tracking_enabled }, conn.live_tracking_enabled ? "Live tracking stopped" : "Live tracking started")} busy={busy} testid={`cl-track-${ltid}`} />
        <Toggle label="Public Live Page" on={conn.public_enabled} onClick={() => set({ public_enabled: !conn.public_enabled }, "Saved")} busy={busy} testid={`cl-public-${ltid}`} />
      </div>

      <div className="flex flex-wrap gap-2 text-sm">
        <button onClick={syncNow} disabled={busy} className="inline-flex items-center gap-1.5 bg-[#1E63C4] text-white rounded-md px-3 py-2 font-semibold hover:bg-[#174e9c] disabled:opacity-60" data-testid={`cl-sync-${ltid}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync now</button>
        <button onClick={loadLogs} className="inline-flex items-center gap-1.5 border border-[#E5E7EB] rounded-md px-3 py-2 hover:bg-[#F9FAF8]" data-testid={`cl-logs-${ltid}`}>Sync log</button>
        <button onClick={remove} disabled={busy} className="inline-flex items-center gap-1.5 border border-red-200 text-red-600 rounded-md px-3 py-2 hover:bg-red-50"><Trash2 className="h-4 w-4" /> Disconnect</button>
      </div>

      <div className="text-xs text-[#777] flex flex-wrap gap-x-4 gap-y-1">
        <span>Last sync: {conn.last_successful_sync_at ? new Date(conn.last_successful_sync_at).toLocaleString("en-CA", { timeZone: "America/Toronto" }) : "—"}</span>
        <span>Interval: {conn.sync_interval_seconds}s</span>
        <span className="font-semibold">API this month: {usage} / {limit}</span>
      </div>

      {showLog && (
        <div className="rounded-md border border-[#E5E7EB] bg-[#F9FAF8] p-3 max-h-56 overflow-y-auto text-xs font-mono space-y-1" data-testid={`cl-log-${ltid}`}>
          {logs.length === 0 ? <p className="text-[#999]">No requests logged yet.</p> : logs.map((l, i) => (
            <div key={i} className="flex justify-between gap-2">
              <span>{new Date(l.ts).toLocaleTimeString("en-CA", { timeZone: "America/Toronto" })} · {l.resource} · {l.type}</span>
              <span className={l.ok ? "text-green-700" : "text-red-600"}>{l.status}{l.counted ? " · 1 req" : ""}</span>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-md border border-[#E5E7EB] p-3 space-y-2">
        <p className="text-xs font-semibold text-[#555] flex items-center gap-1.5"><Youtube className="h-4 w-4 text-[#E11D2A]" /> YouTube livestream URL</p>
        <div className="flex gap-2">
          <input className={input} placeholder="Paste any YouTube link (watch, youtu.be, /live…)" value={stream} onChange={(e) => setStream(e.target.value)} data-testid={`cl-stream-input-${ltid}`} />
          <button onClick={() => set({ livestream_url: stream.trim() }, "Livestream link saved")} disabled={busy || stream.trim() === (conn.livestream_url || "")} className="shrink-0 inline-flex items-center gap-1.5 text-sm bg-[#1E63C4] text-white rounded-md px-3 py-2 font-semibold hover:bg-[#174e9c] disabled:opacity-50" data-testid={`cl-stream-save-${ltid}`}>Save</button>
        </div>
        <p className="text-[11px] text-[#999]">When set, a red "Watch Live Stream" button appears on the public live page (hidden once the tournament is complete).</p>
      </div>

      <div className="rounded-md border border-[#E5E7EB] p-3 space-y-2">
        <p className="text-xs font-semibold text-[#555]">Post a manual live update</p>
        <input className={input} placeholder="Title (e.g. 15-minute break)" value={feedTitle} onChange={(e) => setFeedTitle(e.target.value)} data-testid={`cl-feed-title-${ltid}`} />
        <textarea className={input} rows={2} placeholder="Message (optional)" value={feedBody} onChange={(e) => setFeedBody(e.target.value)} data-testid={`cl-feed-body-${ltid}`} />
        <button onClick={postFeed} disabled={busy} className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]"><Plus className="h-4 w-4" /> Post update</button>
      </div>

      <SocialCards conn={conn} />
    </div>
  );
}

function Toggle({ label, on, onClick, busy, testid }) {
  return (
    <button onClick={onClick} disabled={busy} data-testid={testid} className={`flex items-center justify-between rounded-md border px-4 py-3 text-sm font-semibold transition-colors ${on ? "border-green-300 bg-green-50 text-green-700" : "border-[#E5E7EB] bg-white text-[#555]"}`}>
      {label}
      <span className={`inline-flex items-center gap-1 ${on ? "text-green-700" : "text-[#999]"}`}>{on ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />} {on ? "ON" : "OFF"}</span>
    </button>
  );
}

function SocialCards({ conn }) {
  const liveUrl = `https://quintebca.ca/tournaments/${conn.local_tournament_id}/live`;
  const cards = [
    { key: "live", text: `\uD83D\uDD34 ${conn.name} is LIVE!\n\nFollow every match live:\n${liveUrl}` },
    { key: "final", text: `\uD83C\uDFC6 ${conn.name} — FINAL SET!\n\nThe championship match is set. Watch it live:\n${liveUrl}` },
  ];
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  return (
    <div className="rounded-md border border-[#E5E7EB] p-3 space-y-2">
      <p className="text-xs font-semibold text-[#555]">Social copy (paste to Facebook)</p>
      {cards.map((c) => (
        <div key={c.key} className="flex items-start gap-2">
          <pre className="flex-1 whitespace-pre-wrap text-xs bg-[#F9FAF8] rounded p-2 border border-[#eee]">{c.text}</pre>
          <button onClick={() => copy(c.text)} className="text-[#1E63C4] p-1.5" title="Copy" data-testid={`cl-social-${c.key}-${conn.local_tournament_id}`}><Copy className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}

export function ChallongeManager() {
  const [state, setState] = useState({ connections: [], usage: 0, limit: 500 });
  const [loading, setLoading] = useState(true);
  const [ltid, setLtid] = useState("");
  const [ref, setRef] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [found, setFound] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/challonge/connections");
    setState(data); setLoading(false);
  }, []);
  useEffect(() => { load().catch(() => setLoading(false)); }, [load]);

  const test = async () => { setTesting(true); try { await api.get("/admin/challonge/test"); toast.success("Challonge connection OK"); } catch (e) { toast.error(formatApiError(e)); } finally { setTesting(false); } };
  const find = async () => { setTesting(true); try { const { data } = await api.get("/admin/challonge/find"); setFound(data.tournaments); } catch (e) { toast.error(formatApiError(e)); } finally { setTesting(false); } };
  const connect = async () => {
    if (!ltid.trim() || !ref.trim()) return toast.error("Enter a local ID and a Challonge URL/slug");
    setConnecting(true);
    try { await api.post("/admin/challonge/connect", { local_tournament_id: ltid.trim(), url_or_slug: ref.trim() }); toast.success("Tournament connected"); setLtid(""); setRef(""); setFound(null); await load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setConnecting(false); }
  };

  if (loading) return <p className="text-[#555]">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="challonge-manager">
      <div>
        <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Radio className="h-6 w-6 text-[#E11D2A]" /> Live Tournament Centre</h2>
        <p className="text-sm text-[#555]">Connect a Challonge tournament, then start live tracking. The public live page stays off until you enable it.</p>
      </div>

      <div className="rounded-lg border border-[#E5E7EB] p-5 space-y-3">
        <h3 className="font-heading font-bold">Connect a Challonge tournament</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={test} disabled={testing} className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-2 hover:bg-[#F9FAF8]" data-testid="cl-test-btn">{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Test connection</button>
          <button onClick={find} disabled={testing} className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-2 hover:bg-[#F9FAF8]" data-testid="cl-find-btn"><Search className="h-4 w-4" /> Find my tournaments</button>
          <span className="text-xs text-[#777] ml-auto font-semibold">API this month: {state.usage} / {state.limit}</span>
        </div>
        {found && (
          <select className={input} data-testid="cl-found-select" onChange={(e) => setRef(e.target.value)} defaultValue="">
            <option value="" disabled>Select a tournament…</option>
            {found.map((t) => <option key={t.id} value={t.slug || t.url}>{t.name} ({t.state})</option>)}
          </select>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-[#555] mb-1">Local ID / slug (used in the live URL)</label><input className={input} placeholder="e.g. u650-aug" value={ltid} onChange={(e) => setLtid(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} data-testid="cl-ltid-input" /></div>
          <div><label className="block text-xs font-medium text-[#555] mb-1">Challonge URL or slug</label><input className={input} placeholder="challonge.com/xxxx or xxxx" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="cl-ref-input" /></div>
        </div>
        <button onClick={connect} disabled={connecting} className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-5 py-2.5 font-semibold hover:bg-[#174e9c] disabled:opacity-60" data-testid="cl-connect-btn">{connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Connect</button>
      </div>

      {state.connections.length === 0 ? (
        <p className="text-[#777] text-sm">No tournaments connected yet.</p>
      ) : (
        <div className="space-y-4">
          {state.connections.map((c) => <ConnectionCard key={c.local_tournament_id} conn={c} usage={state.usage} limit={state.limit} onChange={load} />)}
        </div>
      )}
    </div>
  );
}
